import { useSeo } from "@/lib/seo";
import { IMG } from "@/lib/constants";
import { Overline, Reveal, CTAButton, Section } from "@/components/shared";
import { PageHero, FAQ, StepList } from "@/components/PageParts";
import { StateTransition } from "@/components/Diagrams";

const mechanisms = [
  "DNA damage and repair", "Mitochondrial function", "Oxidative stress", "Nutrient sensing",
  "Inflammation", "Autophagy", "Proteostasis", "Epigenetic regulation",
  "Cellular senescence", "Apoptosis", "Resilience and recovery",
];

const research_steps = [
  { t: "Integrate", d: "Published biology, experimental data and established mechanisms." },
  { t: "Model", d: "Logical, mathematical and computational representations." },
  { t: "Simulate", d: "Perturbations, scenarios and interventions." },
  { t: "Test", d: "Compare model behavior with published evidence and identify predictions for future validation." },
];

const current_work = [
  "Refining the biological network",
  "Testing alternative regulatory assumptions",
  "Analyzing stable cellular states",
  "Simulating genetic and therapeutic perturbations",
  "Identifying influential pathways",
  "Comparing model behavior with published evidence",
  "Progressing toward continuous and stochastic models",
];

const collaborators = [
  "Aging biologists", "Systems biologists", "Computational scientists", "Mathematicians",
  "Bioinformaticians", "Laboratories", "Institutions", "Funders", "Aligned collaborators",
];

const faqs = [
  { q: "Is the research open?", a: "The Beyond Life program is developed as an independent, open-minded research initiative. We share updates and reasoning publicly and welcome scrutiny and collaboration." },
  { q: "Can I access the models and code?", a: "Model documentation and selected code will be shared progressively as the project matures. Reach out if you have a specific research interest." },
  { q: "Are the models experimentally validated?", a: "The models integrate published evidence and are used to generate testable predictions. They are not a substitute for experimental validation, which is part of future work." },
  { q: "What stage is the project in?", a: "The first prototype uses a logical network to study how signals produce cellular states. Work is ongoing toward continuous and stochastic models." },
  { q: "Who can collaborate?", a: "Aging and systems biologists, computational scientists, mathematicians, bioinformaticians, laboratories, institutions and aligned funders." },
  { q: "What kinds of data or expertise are useful?", a: "Curated mechanistic data, published datasets, domain expertise on specific pathways, and computational or mathematical methods." },
  { q: "How can I support the project?", a: "Through collaboration, data sharing, methodological input or funding. Use the contact form to propose a collaboration." },
];

export default function IndependentResearch() {
  useSeo(
    "Aging as a Complex System | Systems Beyond",
    "Systems Beyond develops mathematical and computational models of cellular aging, senescence, adaptation, dysfunction and biological transitions."
  );

  return (
    <div>
      <PageHero
        overline="Independent Research"
        title="Understanding aging as a complex system."
        subtitle="Systems Beyond develops mathematical and computational models to study how interacting biological mechanisms drive cellular aging, adaptation, dysfunction, senescence and death."
      >
        <div className="mt-10 flex flex-wrap gap-3">
          <CTAButton to="/simulator" variant="accent" testId="ir-hero-simulator">Launch the Simulator</CTAButton>
          <CTAButton to="/contact" variant="secondary" testId="ir-propose-collab">Propose a Collaboration</CTAButton>
        </div>
      </PageHero>

      {/* Research at the center */}
      <Section className="py-16 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <Overline>Research at the center</Overline>
            <h2 className="mt-6 text-3xl lg:text-4xl tracking-tight leading-tight font-medium text-ink">
              Not only a consultancy — an independent research initiative.
            </h2>
            <p className="mt-6 text-dark-gray leading-relaxed">
              Systems Beyond is built to investigate difficult questions that require long-term,
              integrative and computational thinking. Research is the intellectual center of our
              work; our consulting services are practical applications of the same capabilities.
            </p>
          </div>
          <div className="lg:col-span-6">
            <div className="border border-silver/50 bg-off-white relative">
              <img src={IMG.cell} alt="Abstract representation of a digital human somatic cell with internal signaling network" className="w-full h-auto" />
              <span className="absolute top-3 left-3 font-mono-sci text-[10px] text-dark-gray">FIG.03 — DIGITAL CELL</span>
            </div>
          </div>
        </div>
      </Section>

      {/* Why aging */}
      <Section dark className="py-20 lg:py-28" grid>
        <div className="max-w-2xl">
          <Overline dark>Why aging?</Overline>
          <h2 className="mt-6 text-3xl lg:text-4xl tracking-tight leading-tight font-medium">
            An emergent process, not a single pathway.
          </h2>
          <p className="mt-6 text-white/60 leading-relaxed">
            Aging emerges from the interaction of many mechanisms. The point is not one pathway but
            the interaction network between them.
          </p>
        </div>
        <div className="mt-12 flex flex-wrap gap-2">
          {mechanisms.map((m) => (
            <span key={m} className="font-mono-sci text-xs text-white/70 border border-white/15 px-4 py-2.5 hover:border-acid hover:text-white transition-colors">
              {m}
            </span>
          ))}
        </div>
      </Section>

      {/* Beyond Life + digital cell */}
      <Section className="py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-6">
          <Reveal>
            <div className="border border-silver/50 p-8 lg:p-10 bg-off-white h-full">
              <span className="font-mono-sci text-xs text-blue-gray">FLAGSHIP PROGRAM</span>
              <h3 className="mt-4 text-2xl lg:text-3xl text-ink">Beyond Life</h3>
              <p className="mt-4 text-dark-gray leading-relaxed">
                Beyond Life is the independent research program dedicated to modeling somatic-cell
                aging as a dynamic system.
              </p>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="border border-silver/50 p-8 lg:p-10 bg-off-white h-full">
              <span className="font-mono-sci text-xs text-blue-gray">THE DIGITAL CELL</span>
              <h3 className="mt-4 text-2xl lg:text-3xl text-ink">A computational human somatic cell</h3>
              <p className="mt-4 text-dark-gray leading-relaxed">
                We are developing a computational representation of a human somatic cell where
                aging-related pathways interact over time.
              </p>
            </div>
          </Reveal>
        </div>

        <div className="mt-8 border border-silver/50 p-8 lg:p-10">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
            <div>
              <span className="font-mono-sci text-[10px] text-dark-gray">FIG.04 — MODELED STATE TRANSITIONS</span>
              <p className="mt-2 text-sm text-dark-gray max-w-md leading-relaxed">Explore these dynamics yourself in the interactive Boolean-network prototype.</p>
            </div>
            <CTAButton to="/simulator" variant="accent" testId="ir-launch-simulator">Launch the Simulator</CTAButton>
          </div>
          <div className="mt-8"><StateTransition /></div>
        </div>
      </Section>

      {/* How research works */}
      <Section className="pb-20 lg:pb-28">
        <Overline>How the research works</Overline>
        <div className="mt-10"><StepList steps={research_steps} /></div>
      </Section>

      {/* Current phase */}
      <Section className="pb-20 lg:pb-28" grid>
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Overline>Current phase</Overline>
            <h2 className="mt-6 text-3xl lg:text-4xl tracking-tight leading-tight font-medium text-ink">
              A logical network prototype.
            </h2>
            <p className="mt-6 text-dark-gray leading-relaxed">
              The first prototype uses a logical network to study how environmental and intracellular
              signals produce cellular states.
            </p>
          </div>
          <ul className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-silver/40 border border-silver/40">
            {current_work.map((w) => (
              <li key={w} className="bg-white p-6 flex items-start gap-3">
                <span className="w-1.5 h-1.5 bg-acid rounded-full mt-2 shrink-0" />
                <span className="text-sm text-dark-gray leading-relaxed">{w}</span>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      {/* Collaboration */}
      <Section dark className="py-20 lg:py-28">
        <div className="max-w-2xl">
          <Overline dark>Collaboration</Overline>
          <h2 className="mt-6 text-3xl lg:text-4xl tracking-tight leading-tight font-medium">
            We are looking for aligned collaborators.
          </h2>
        </div>
        <div className="mt-10 flex flex-wrap gap-2">
          {collaborators.map((c) => (
            <span key={c} className="font-mono-sci text-xs text-white/70 border border-white/15 px-4 py-2.5">{c}</span>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap gap-3">
          <CTAButton to="/contact" variant="accent" testId="ir-collab-cta">Propose a Collaboration</CTAButton>
          <CTAButton to="/articles" variant="dark" testId="ir-updates-cta">View Research Updates</CTAButton>
        </div>
      </Section>

      {/* FAQ */}
      <Section className="py-20 lg:py-28">
        <div className="max-w-2xl mb-10">
          <Overline>FAQ</Overline>
          <h2 className="mt-6 text-3xl lg:text-4xl tracking-tight leading-tight font-medium text-ink">Questions about the research.</h2>
        </div>
        <FAQ items={faqs} />
      </Section>
    </div>
  );
}
